import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Home, 
  Users, 
  Briefcase, 
  Target, 
  Network, 
  Menu,
  X,
  User,
  LogOut,
  LogIn,
  Sun,
  Moon,
  TrendingUp,
  Shield,
  Sparkles,
  Database,
  Edit3
} from 'lucide-react';
import OnboardingModal from './OnboardingModal';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentUser, isAdmin, logout, openOnboarding } = useAuth();

  const isProfileIncomplete = currentUser && (!currentUser.skills || currentUser.skills.length === 0);

  const navigation = currentUser ? [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { 
      name: 'My Profile & Skills', 
      href: '/me', 
      icon: User, 
      highlightBadge: isProfileIncomplete ? 'Setup' : null 
    },
    { name: isAdmin ? 'All Projects' : 'Projects & Apply', href: '/projects', icon: Briefcase },
    { name: 'People Directory', href: '/people', icon: Users },
    { name: 'Graph Explorer', href: '/graph', icon: Network },
    ...(isAdmin ? [
      { name: 'Skills Directory', href: '/skills', icon: Target },
      { name: 'Matching Engine', href: '/match', icon: Target },
      { name: 'Enhanced Analytics', href: '/enhanced', icon: TrendingUp },
      { name: 'Database & Users', href: '/database', icon: Database },
    ] : [])
  ] : [
    { name: 'Sign In / Register', href: '/login', icon: LogIn }
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDarkMode ? 'bg-dark-950' : 'bg-light-50'}`}>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
        <div className={`fixed left-0 top-0 h-full w-64 border-r transition-colors duration-200 overflow-y-auto ${
          isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-xl'
        }`}>
          <div className={`flex items-center justify-between p-6 border-b transition-colors duration-200 ${
            isDarkMode ? 'border-dark-800' : 'border-light-200'
          }`}>
            <Link to="/" onClick={() => setSidebarOpen(false)}>
              <h1 className="text-2xl font-bold gradient-text">SkillMesh</h1>
            </Link>
            <button onClick={() => setSidebarOpen(false)}>
              <X className={`h-6 w-6 transition-colors duration-200 ${
                isDarkMode ? 'text-white' : 'text-light-700'
              }`} />
            </button>
          </div>
          <nav className="p-4 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-gradient-primary text-white shadow-lg'
                    : isDarkMode 
                      ? 'text-dark-300 hover:text-white hover:bg-dark-800'
                      : 'text-light-600 hover:text-light-900 hover:bg-light-100 hover:shadow-md'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </div>
                {item.highlightBadge && (
                  <span className="text-[10px] bg-amber-500/20 text-amber-300 font-bold px-1.5 py-0.5 rounded border border-amber-500/30 animate-pulse">
                    {item.highlightBadge}
                  </span>
                )}
                {item.adminHighlight && (
                  <span className="text-[10px] bg-purple-500/20 text-purple-400 font-bold px-1.5 py-0.5 rounded border border-purple-500/30">
                    Admin
                  </span>
                )}
              </Link>
            ))}
            {!currentUser && (
              <div className={`p-4 my-3 rounded-xl border text-xs text-center ${
                isDarkMode 
                  ? 'bg-dark-800/80 border-dark-700 text-dark-300' 
                  : 'bg-light-100 border-light-200 text-light-600'
              }`}>
                <Shield className="h-6 w-6 text-primary-400 mx-auto mb-2" />
                <div className="font-semibold text-sm mb-1">Access Restricted</div>
                <p className="text-[11px] leading-relaxed">
                  Sign in to view projects, matching, skill graphs, and team data.
                </p>
              </div>
            )}
          </nav>

          {/* User profile section mobile */}
          <div className={`p-4 border-t transition-colors duration-200 ${
            isDarkMode ? 'border-dark-800' : 'border-light-200'
          }`}>
            {currentUser ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between px-2">
                  <div className="flex items-center space-x-2 truncate">
                    <User className="h-5 w-5 text-primary-400" />
                    <div className="truncate">
                      <div className="text-sm font-semibold text-white truncate">{currentUser.name}</div>
                      <div className="text-xs text-gray-400 truncate">{currentUser.email}</div>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                    isAdmin ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {isAdmin ? 'Admin' : 'User'}
                  </span>
                </div>
                <Link
                  to="/me"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center space-x-3 px-4 py-2 text-sm rounded-lg hover:bg-dark-800 text-dark-300 hover:text-white"
                >
                  <User className="h-4 w-4" />
                  <span>My Profile & Skills</span>
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setSidebarOpen(false);
                    navigate('/login');
                  }}
                  className="flex items-center space-x-3 px-4 py-2 text-sm rounded-lg hover:bg-dark-800 text-red-400 w-full"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => setSidebarOpen(false)}
                className="btn-primary w-full flex items-center justify-center space-x-2 py-2"
              >
                <LogIn className="h-4 w-4" />
                <span>Sign In / Magic Link</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className={`flex flex-col flex-grow border-r transition-colors duration-200 ${
          isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-xl'
        }`}>
          <div className={`flex items-center justify-between p-6 border-b transition-colors duration-200 ${
            isDarkMode ? 'border-dark-800' : 'border-light-200'
          }`}>
            <Link to="/">
              <h1 className="text-2xl font-bold gradient-text">SkillMesh</h1>
            </Link>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-200 ${
                isDarkMode 
                  ? 'bg-dark-800 hover:bg-dark-700 text-dark-300 hover:text-white'
                  : 'bg-light-100 hover:bg-light-200 text-light-600 hover:text-light-900 shadow-sm hover:shadow-md'
              }`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive(item.href)
                    ? 'bg-gradient-primary text-white shadow-lg'
                    : isDarkMode 
                      ? 'text-dark-300 hover:text-white hover:bg-dark-800'
                      : 'text-light-600 hover:text-light-900 hover:bg-light-100 hover:shadow-md'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </div>
                {item.highlightBadge && (
                  <span className="text-[10px] bg-amber-500/20 text-amber-300 font-bold px-1.5 py-0.5 rounded border border-amber-500/30 animate-pulse">
                    {item.highlightBadge}
                  </span>
                )}
                {item.adminHighlight && (
                  <span className="text-[10px] bg-purple-500/20 text-purple-400 font-bold px-1.5 py-0.5 rounded border border-purple-500/30">
                    Admin
                  </span>
                )}
              </Link>
            ))}
            {!currentUser && (
              <div className={`p-4 my-3 rounded-xl border text-xs text-center ${
                isDarkMode 
                  ? 'bg-dark-800/80 border-dark-700 text-dark-300' 
                  : 'bg-light-100 border-light-200 text-light-600'
              }`}>
                <Shield className="h-6 w-6 text-primary-400 mx-auto mb-2" />
                <div className="font-semibold text-sm mb-1">Access Restricted</div>
                <p className="text-[11px] leading-relaxed">
                  Sign in to view projects, matching, skill graphs, and team data.
                </p>
              </div>
            )}
          </nav>

          {/* Sidebar Footer / User Profile & Role Switch */}
          <div className={`p-4 border-t transition-colors duration-200 ${
            isDarkMode ? 'border-dark-800' : 'border-light-200'
          }`}>
            {currentUser ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 truncate">
                    <User className="h-5 w-5 text-primary-400" />
                    <div className="truncate">
                      <div className="text-sm font-semibold truncate">{currentUser.name}</div>
                      <div className="text-xs text-gray-400 truncate">{currentUser.email}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${
                    isAdmin ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  }`}>
                    {isAdmin ? 'Admin' : 'User'}
                  </span>
                </div>

                <div className="space-y-1">
                  <Link
                    to="/me"
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      isActive('/me')
                        ? 'bg-gradient-primary text-white shadow-lg'
                        : isDarkMode
                          ? 'text-dark-300 hover:text-white hover:bg-dark-800'
                          : 'text-light-600 hover:text-light-900 hover:bg-light-100'
                    }`}
                  >
                    <User className="h-4 w-4" />
                    <span>My Profile & Skills</span>
                  </Link>

                  <button
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 w-full text-left ${
                      isDarkMode
                        ? 'text-red-400 hover:bg-dark-800'
                        : 'text-red-500 hover:bg-light-100'
                    }`}
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-primary w-full flex items-center justify-center space-x-2 py-2"
              >
                <LogIn className="h-4 w-4" />
                <span>Sign In / Magic Link</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Desktop top header */}
        <header className={`hidden lg:flex items-center justify-between px-8 py-3.5 border-b transition-colors duration-200 ${
          isDarkMode ? 'border-dark-800 bg-dark-900/60 backdrop-blur-md' : 'border-light-200 bg-white/80 backdrop-blur-md'
        }`}>
          <div className="flex items-center space-x-3">
            <span className="text-xs uppercase tracking-wider font-semibold text-primary-400">
              SkillMesh Network
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-xs text-gray-400">
              {location.pathname === '/me' ? 'My Profile & Skills' :
               location.pathname.startsWith('/projects') ? 'Collaborative Projects' :
               location.pathname === '/dashboard' ? 'Personal Workspace' :
               location.pathname === '/people' ? 'Talent Directory' :
               'Platform'}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            {currentUser ? (
              <div className="flex items-center space-x-3">
                {isProfileIncomplete && (
                  <button
                    onClick={openOnboarding}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 flex items-center space-x-1.5 transition-all animate-pulse"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Complete Profile</span>
                  </button>
                )}

                <Link
                  to="/me"
                  className={`flex items-center space-x-2.5 px-3 py-1.5 rounded-xl border transition-all ${
                    isActive('/me')
                      ? 'border-primary-500/50 bg-primary-500/10 shadow-sm'
                      : isDarkMode
                        ? 'border-dark-700 bg-dark-800/80 hover:border-dark-600 hover:text-white'
                        : 'border-light-200 bg-light-100 hover:border-light-300 hover:text-light-950'
                  }`}
                  title="View and edit your profile"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-primary flex items-center justify-center text-xs font-bold text-white shadow-sm">
                    {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="text-left text-xs">
                    <div className="font-semibold truncate max-w-[120px]">{currentUser.name || 'My Profile'}</div>
                    <div className="text-[10px] text-primary-400 truncate max-w-[120px]">{currentUser.title || 'Member'}</div>
                  </div>
                  <Edit3 className="h-3.5 w-3.5 text-gray-400 ml-1" />
                </Link>
              </div>
            ) : (
              <Link to="/login" className="btn-primary py-1.5 px-3.5 text-xs flex items-center space-x-1.5">
                <LogIn className="h-3.5 w-3.5" />
                <span>Sign In</span>
              </Link>
            )}
          </div>
        </header>

        {/* Mobile header */}
        <div className={`lg:hidden flex items-center justify-between p-4 border-b transition-colors duration-200 ${
          isDarkMode ? 'border-dark-800 bg-dark-900' : 'border-light-200 bg-white shadow-sm'
        }`}>
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className={`h-6 w-6 transition-colors duration-200 ${
              isDarkMode ? 'text-white' : 'text-light-700'
            }`} />
          </button>
          <Link to="/">
            <h1 className="text-xl font-bold gradient-text">SkillMesh</h1>
          </Link>
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white"
          >
            {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* Onboarding Wizard Modal */}
      <OnboardingModal />
    </div>
  );
};

export default Layout;
